import { Link } from 'react-router-dom';
import { Star, Archive, ExternalLink, BookOpen, Trash2, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActionButton } from './ActionButton';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { trackLinkCardClick, trackEvent, AnalyticsEvent } from '@/lib/analytics';
import type { LinkResponse } from '@readlater/shared';

interface LinkCardProps {
  link: LinkResponse;
  onToggleFavorite?: (id: number) => void;
  onToggleArchive?: (id: number) => void;
  onToggleRead?: (id: number) => void;
  onDelete?: (id: number) => void;
}

/**
 * Get the summary text to display, with fallback logic
 */
function getSummaryText(link: LinkResponse): string {
  if (link.summary) {
    return link.summary;
  }
  if (link.content) {
    // Return first ~150 chars of content as preview
    return link.content.slice(0, 150) + (link.content.length > 150 ? '...' : '');
  }
  // Placeholder for processing/pending states
  return 'Extracting content...';
}

/**
 * Build metadata items array for display (excludes tags)
 */
function getMetadataItems(link: LinkResponse): string[] {
  const items: string[] = [];

  // Domain
  if (link.source_domain) {
    items.push(link.source_domain);
  }

  // Reading time
  if (link.reading_time) {
    items.push(`${link.reading_time} min`);
  }

  // Status (if not processed)
  if (link.status === 'processing') {
    items.push('Processing...');
  } else if (link.status === 'pending') {
    items.push('Pending...');
  } else if (link.status === 'failed') {
    items.push('Failed');
  }

  // Archived label
  if (link.is_archived) {
    items.push('Archived');
  }

  return items;
}

/**
 * Get tags to display (first 3, with +N overflow indicator)
 */
function getDisplayTags(link: LinkResponse): { tags: string[]; overflow: number } {
  if (!link.tags || link.tags.length === 0) {
    return { tags: [], overflow: 0 };
  }
  const tags = link.tags.slice(0, 3);
  const overflow = Math.max(0, link.tags.length - 3);
  return { tags, overflow };
}

export function LinkCard({
  link,
  onToggleFavorite,
  onToggleArchive,
  onToggleRead,
  onDelete,
}: LinkCardProps) {
  const { registerLinkClick } = useAnalytics();
  const isUnread = !link.is_read;

  const handleLinkClick = () => {
    // Track the click and register the tracker for the detail page to use
    const tracker = trackLinkCardClick(link.id, link.title);
    registerLinkClick(link.id, tracker);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent(AnalyticsEvent.LINK_CARD_FAVORITE, {
      linkId: link.id,
      isFavorite: !link.is_favorite,
    });
    onToggleFavorite?.(link.id);
  };

  const handleArchiveClick = () => {
    trackEvent(AnalyticsEvent.LINK_CARD_ARCHIVE, {
      linkId: link.id,
      isArchived: !link.is_archived,
    });
    onToggleArchive?.(link.id);
  };

  const handleExternalClick = () => {
    trackEvent(AnalyticsEvent.LINK_CARD_EXTERNAL, {
      linkId: link.id,
      url: link.url,
    });
  };

  const handleReadClick = () => {
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'toggle_read',
      linkId: link.id,
      isRead: !link.is_read,
    });
    onToggleRead?.(link.id);
  };

  const handleDeleteClick = () => {
    trackEvent(AnalyticsEvent.LINK_DETAIL_ACTION, {
      action: 'delete',
      linkId: link.id,
    });
    onDelete?.(link.id);
  };

  const metadataItems = getMetadataItems(link);
  const { tags: displayTags, overflow: tagOverflow } = getDisplayTags(link);
  const summaryText = getSummaryText(link);

  return (
    <Link to={`/links/${link.id}`} className="group block" onClick={handleLinkClick}>
      <div
        className={cn(
          'relative flex items-start gap-3 px-4 py-5 transition-colors duration-200',
          'active:bg-muted/30',
          isUnread ? 'opacity-100' : 'opacity-90'
        )}
      >
        {/* Unread indicator dot */}
        <div className="flex-shrink-0 pt-2">
          {isUnread ? (
            <div className="h-2 w-2 rounded-full bg-primary" aria-label="Unread" />
          ) : (
            <div className="h-2 w-2" aria-hidden="true" />
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Title */}
          <h3
            className={cn(
              'line-clamp-1 md:line-clamp-2 font-serif text-[20px] leading-tight transition-colors duration-200',
              isUnread
                ? 'font-semibold text-foreground group-hover:text-[hsl(var(--link-hover))]'
                : 'font-normal text-muted-foreground group-hover:text-[hsl(var(--link-hover))]'
            )}
          >
            {link.title || link.url}
          </h3>

          {/* Summary - always show with fallback */}
          <p className="mt-2 line-clamp-2 text-[15px] leading-relaxed text-muted-foreground/80 transition-colors duration-200">
            {summaryText}
          </p>

          {/* Metadata line */}
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            {metadataItems.map((item, index) => (
              <span key={index} className="flex items-center gap-1.5">
                {index > 0 && <span aria-hidden="true">·</span>}
                <span>{item}</span>
              </span>
            ))}
            {/* Tags with pill treatment */}
            {displayTags.length > 0 && (
              <>
                {metadataItems.length > 0 && <span aria-hidden="true">·</span>}
                <span className="flex items-center gap-1">
                  {displayTags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                  {tagOverflow > 0 && <span className="text-muted-foreground">+{tagOverflow}</span>}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Trailing accessories - hidden on mobile where swipe handles actions */}
        <div className="hidden md:flex items-center gap-1 flex-shrink-0">
          {/* Favorite button */}
          <ActionButton
            aria-label={link.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
            onClick={handleFavoriteClick}
          >
            <Star className={cn(link.is_favorite && 'fill-yellow-400 !text-yellow-400')} />
          </ActionButton>

          {/* More actions menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ActionButton aria-label="More actions" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal />
              </ActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleExternalClick();
                  window.open(link.url, '_blank');
                }}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Original
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleReadClick();
                }}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                {link.is_read ? 'Mark as Unread' : 'Mark as Read'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveClick();
                }}
              >
                <Archive className={cn('mr-2 h-4 w-4', link.is_archived && 'text-primary')} />
                {link.is_archived ? 'Unarchive' : 'Archive'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Apple-style inset separator: aligned to text start, extends to trailing edge */}
        <div className="absolute bottom-0 right-0 left-[36px] h-px bg-border" />
      </div>
    </Link>
  );
}
